const API_BASE = '/api/v1'

export interface User {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  createdAt?: string
  isGlobalAdmin?: boolean
}

export interface Membership {
  orgId: string
  orgSlug: string
  orgName?: string
  role: 'admin' | 'approver' | 'special_guest' | 'guest'
  accountType: 'guest' | 'room_owner'
  status?: string
  canBookOutOfHours?: boolean
  attributes?: {
    requiresApproval?: boolean
    discountCode?: string | null
    canBookDirectly?: boolean
  }
  joinedAt?: string
  roomsiFeePercent?: string
}

export interface AuthResponse {
  user: User
  memberships: Membership[]
  accessToken: string
  organisation?: {
    id: string
    name: string
    slug: string
  }
}

export interface MeResponse {
  user: User
  memberships: Membership[]
  currentOrg?: {
    orgId: string
    orgSlug: string
    role: 'admin' | 'approver' | 'special_guest' | 'guest'
    accountType: 'guest' | 'room_owner'
  }
}

class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Automatic token refresh handling
let refreshPromise: Promise<boolean> | null = null

async function tryRefreshToken(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    return response.ok
  } catch {
    return false
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json()

  if (!response.ok) {
    // Handle validation errors (zod returns error as object with issues)
    let errorMessage = 'An error occurred'
    if (typeof data.error === 'string') {
      errorMessage = data.error
    } else if (data.error?.issues) {
      // Zod validation error - extract first issue message
      errorMessage = data.error.issues[0]?.message || 'Validation failed'
    } else if (typeof data.error === 'object') {
      errorMessage = JSON.stringify(data.error)
    }

    throw new ApiError(data.code || 'UNKNOWN_ERROR', errorMessage, response.status)
  }

  return data as T
}

/**
 * Make an authenticated API request with automatic token refresh
 * This wraps fetch and handles 401 errors by attempting to refresh the token
 */
async function apiFetch<T>(url: string, options: RequestInit = {}): Promise<T> {
  const mergedOptions: RequestInit = {
    ...options,
    credentials: 'include',
  }

  let response = await fetch(url, mergedOptions)

  // If we get a 401, try to refresh the token once
  if (response.status === 401) {
    // Use a shared promise to avoid multiple simultaneous refresh attempts
    if (!refreshPromise) {
      refreshPromise = tryRefreshToken().finally(() => {
        refreshPromise = null
      })
    }

    const refreshed = await refreshPromise

    if (refreshed) {
      // Retry the original request
      response = await fetch(url, mergedOptions)
    }
  }

  return handleResponse<T>(response)
}

export const api = {
  auth: {
    async register(data: {
      orgName: string
      email: string
      password: string
      name: string
    }): Promise<AuthResponse> {
      const response = await fetch(`${API_BASE}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
      })
      return handleResponse<AuthResponse>(response)
    },

    async login(data: { email: string; password: string }): Promise<AuthResponse> {
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        credentials: 'include',
      })
      return handleResponse<AuthResponse>(response)
    },

    async logout(): Promise<void> {
      await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      })
    },

    async me(): Promise<MeResponse> {
      return apiFetch<MeResponse>(`${API_BASE}/auth/me`)
    },

    async refresh(): Promise<{ accessToken: string; user: User }> {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      return handleResponse<{ accessToken: string; user: User }>(response)
    },

    async switchOrg(orgSlug: string): Promise<{
      currentOrg: { orgId: string; orgSlug: string; role: string; accountType: string }
    }> {
      const response = await fetch(`${API_BASE}/auth/switch-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgSlug }),
        credentials: 'include',
      })
      return handleResponse<{
        currentOrg: { orgId: string; orgSlug: string; role: string; accountType: string }
      }>(response)
    },
  },

  rooms: {
    async list(): Promise<{ rooms: Room[] }> {
      return apiFetch<{ rooms: Room[] }>(`${API_BASE}/rooms`)
    },

    async get(id: string): Promise<{ room: Room; schedules: RoomSchedule[] }> {
      return apiFetch<{ room: Room; schedules: RoomSchedule[] }>(`${API_BASE}/rooms/${id}`)
    },

    async create(data: CreateRoomData): Promise<{ room: Room; promotedToRoomOwner?: boolean }> {
      return apiFetch<{ room: Room; promotedToRoomOwner?: boolean }>(`${API_BASE}/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async update(id: string, data: Partial<CreateRoomData>): Promise<{ room: Room }> {
      return apiFetch<{ room: Room }>(`${API_BASE}/rooms/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async delete(id: string): Promise<void> {
      await apiFetch<void>(`${API_BASE}/rooms/${id}`, {
        method: 'DELETE',
      })
    },

    async duplicate(id: string): Promise<{ room: Room }> {
      return apiFetch<{ room: Room }>(`${API_BASE}/rooms/${id}/duplicate`, {
        method: 'POST',
      })
    },

    async getSchedule(id: string): Promise<{ schedules: RoomSchedule[] }> {
      return apiFetch<{ schedules: RoomSchedule[] }>(`${API_BASE}/rooms/${id}/schedule`)
    },

    async updateSchedule(
      id: string,
      schedules: Omit<RoomSchedule, 'id' | 'roomId'>[]
    ): Promise<{ schedules: RoomSchedule[] }> {
      return apiFetch<{ schedules: RoomSchedule[] }>(`${API_BASE}/rooms/${id}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedules }),
      })
    },

    // Image management - keep using direct fetch for FormData uploads
    async uploadImages(
      id: string,
      files: File[]
    ): Promise<{ images: { url: string; alt?: string }[] }> {
      const formData = new FormData()
      files.forEach((file) => formData.append('images', file))

      const response = await fetch(`${API_BASE}/rooms/${id}/images`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      return handleResponse<{ images: { url: string; alt?: string }[] }>(response)
    },

    async deleteImage(
      id: string,
      filename: string
    ): Promise<{ images: { url: string; alt?: string }[]; heroImageIndex: number }> {
      return apiFetch<{ images: { url: string; alt?: string }[]; heroImageIndex: number }>(
        `${API_BASE}/rooms/${id}/images/${filename}`,
        {
          method: 'DELETE',
        }
      )
    },

    async setHeroImage(id: string, heroImageIndex: number): Promise<{ heroImageIndex: number }> {
      return apiFetch<{ heroImageIndex: number }>(`${API_BASE}/rooms/${id}/hero-image`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ heroImageIndex }),
      })
    },

    // Blackout management
    async getBlackouts(id: string): Promise<{ blackouts: Blackout[] }> {
      return apiFetch<{ blackouts: Blackout[] }>(`${API_BASE}/rooms/${id}/blackouts`)
    },

    async createBlackout(id: string, data: CreateBlackoutData): Promise<{ blackout: Blackout }> {
      return apiFetch<{ blackout: Blackout }>(`${API_BASE}/rooms/${id}/blackouts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async deleteBlackout(id: string, blackoutId: string): Promise<void> {
      await apiFetch<void>(`${API_BASE}/rooms/${id}/blackouts/${blackoutId}`, {
        method: 'DELETE',
      })
    },

    // Activity log
    async getActivity(id: string, limit?: number): Promise<{ activities: RoomActivity[] }> {
      const params = limit ? `?limit=${limit}` : ''
      return apiFetch<{ activities: RoomActivity[] }>(`${API_BASE}/rooms/${id}/activity${params}`)
    },
  },

  settings: {
    async get(): Promise<{ organisation: Organisation }> {
      return apiFetch<{ organisation: Organisation }>(`${API_BASE}/settings`)
    },

    async update(data: {
      name?: string
      settings?: Partial<OrgSettings>
    }): Promise<{ organisation: Organisation }> {
      return apiFetch<{ organisation: Organisation }>(`${API_BASE}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async getCurrencies(): Promise<{ currencies: Currency[] }> {
      return apiFetch<{ currencies: Currency[] }>(`${API_BASE}/settings/currencies`)
    },

    async checkSlugAvailability(slug: string): Promise<{ available: boolean; error?: string }> {
      return apiFetch<{ available: boolean; error?: string }>(
        `${API_BASE}/settings/slug/check/${encodeURIComponent(slug)}`
      )
    },

    async updateSlug(slug: string): Promise<{ organisation: Organisation }> {
      return apiFetch<{ organisation: Organisation }>(`${API_BASE}/settings/slug`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug }),
      })
    },
  },

  users: {
    async list(): Promise<{ users: OrgUser[] }> {
      return apiFetch<{ users: OrgUser[] }>(`${API_BASE}/users`)
    },

    async get(userId: string): Promise<{ user: OrgUser }> {
      return apiFetch<{ user: OrgUser }>(`${API_BASE}/users/${userId}`)
    },

    async invite(data: {
      email: string
      name: string
      role?: 'admin' | 'approver' | 'guest'
      attributes?: UserAttributes
    }): Promise<{ user: OrgUser; message: string }> {
      return apiFetch<{ user: OrgUser; message: string }>(`${API_BASE}/users/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async update(
      userId: string,
      data: {
        role?: 'admin' | 'approver' | 'guest'
        accountType?: 'guest' | 'room_owner'
        attributes?: UserAttributes
        status?: 'pending' | 'active' | 'suspended'
      }
    ): Promise<{ user: OrgUser }> {
      return apiFetch<{ user: OrgUser }>(`${API_BASE}/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async remove(userId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/users/${userId}`, {
        method: 'DELETE',
      })
    },

    async resendInvite(userId: string): Promise<{ success: boolean; message: string }> {
      return apiFetch<{ success: boolean; message: string }>(
        `${API_BASE}/users/${userId}/resend-invite`,
        {
          method: 'POST',
        }
      )
    },
  },

  // Guest management (all bookers in the org)
  guests: {
    async list(): Promise<{ guests: Guest[] }> {
      return apiFetch<{ guests: Guest[] }>(`${API_BASE}/guests`)
    },

    async get(guestId: string): Promise<{ guest: GuestDetail; bookings: GuestBooking[] }> {
      return apiFetch<{ guest: GuestDetail; bookings: GuestBooking[] }>(
        `${API_BASE}/guests/${guestId}`
      )
    },

    async update(
      guestId: string,
      data: {
        guestType?: 'guest' | 'special_guest'
        skipApproval?: boolean
        discountPercent?: number | null
        canBookOutOfHours?: boolean
      }
    ): Promise<{ guest: Guest }> {
      return apiFetch<{ guest: Guest }>(`${API_BASE}/guests/${guestId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },
  },

  // Unified People management (members + bookers)
  people: {
    async list(orgSlug?: string): Promise<{ people: Person[] }> {
      return apiFetch<{ people: Person[] }>(`${API_BASE}/people`, { headers: orgSlug ? { 'X-Org-Slug': orgSlug } : {} })
    },

    async get(personId: string): Promise<{ person: PersonDetail; bookings: PersonBooking[] }> {
      return apiFetch<{ person: PersonDetail; bookings: PersonBooking[] }>(
        `${API_BASE}/people/${personId}`
      )
    },

    async update(
      personId: string,
      data: {
        role?: 'admin' | 'approver' | 'special_guest' | 'guest'
        skipApproval?: boolean
        discountPercent?: number | null
        canBookOutOfHours?: boolean
        voluntaryDonation?: boolean
        canCreateRecurringBookings?: boolean
        recurringPaymentType?: 'invoice' | 'donation'
        recurringFixedCost?: number | null
        recurringDonationUrl?: string | null
      }
    ): Promise<{ person: Person }> {
      return apiFetch<{ person: Person }>(`${API_BASE}/people/${personId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async invite(data: {
      email: string
      name: string
      role: 'admin' | 'approver' | 'special_guest'
    }): Promise<{ success: boolean; inviteId: string }> {
      return apiFetch<{ success: boolean; inviteId: string }>(`${API_BASE}/people/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async remove(personId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/people/${personId}`, {
        method: 'DELETE',
      })
    },

    async resendInvite(personId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/people/${personId}/resend-invite`, {
        method: 'POST',
      })
    },
  },

  // Public API (no auth required)
  public: {
    async getOrg(slug: string): Promise<{ organisation: PublicOrg }> {
      const response = await fetch(`${API_BASE}/public/org/${slug}`)
      return handleResponse<{ organisation: PublicOrg }>(response)
    },

    async getRooms(orgSlug: string): Promise<PublicRoomsResponse> {
      const response = await fetch(`${API_BASE}/public/org/${orgSlug}/rooms`)
      return handleResponse<PublicRoomsResponse>(response)
    },

    async getRoom(orgSlug: string, roomSlug: string): Promise<PublicRoomResponse> {
      const response = await fetch(`${API_BASE}/public/org/${orgSlug}/rooms/${roomSlug}`)
      return handleResponse<PublicRoomResponse>(response)
    },

    async getRoomAvailability(
      orgSlug: string,
      roomSlug: string,
      start: string,
      end: string
    ): Promise<{ bookings: { startTime: string; endTime: string; status: string }[] }> {
      const response = await fetch(
        `${API_BASE}/public/org/${orgSlug}/rooms/${roomSlug}/availability?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
      )
      return handleResponse<{ bookings: { startTime: string; endTime: string; status: string }[] }>(
        response
      )
    },

    // Get activity types for an organisation (public access)
    async getActivityTypes(orgSlug: string): Promise<{ activityTypes: ActivityType[] }> {
      const response = await fetch(`${API_BASE}/public/org/${orgSlug}/activity-types`)
      return handleResponse<{ activityTypes: ActivityType[] }>(response)
    },

    // Get terms of use applicable to a room (public access)
    async getRoomTerms(orgSlug: string, roomSlug: string): Promise<{ terms: TermsOfUse[] }> {
      const response = await fetch(`${API_BASE}/public/org/${orgSlug}/rooms/${roomSlug}/terms`)
      return handleResponse<{ terms: TermsOfUse[] }>(response)
    },

    async createGuestBooking(
      orgSlug: string,
      roomId: string,
      data: {
        email: string
        name: string
        phone?: string
        startTime: string
        endTime: string
        title?: string
        notes?: string
        activityTypeId?: string
        activityDescription?: string
        expectedAttendees?: number
        acceptedTermsIds?: string[]
        pliCertificateId?: string
      }
    ): Promise<{
      success: boolean
      bookingId: string
      isNewUser: boolean
      requiresRegistration: boolean
      message: string
    }> {
      const response = await fetch(`${API_BASE}/public/org/${orgSlug}/rooms/${roomId}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
      return handleResponse<{
        success: boolean
        bookingId: string
        isNewUser: boolean
        requiresRegistration: boolean
        message: string
      }>(response)
    },
  },

  bookings: {
    async list(params?: {
      start?: string
      end?: string
      roomId?: string
      includeCancelled?: boolean
      myBookingsOnly?: boolean
    }): Promise<{ bookings: Booking[] }> {
      const searchParams = new URLSearchParams()
      if (params?.start) searchParams.set('start', params.start)
      if (params?.end) searchParams.set('end', params.end)
      if (params?.roomId) searchParams.set('roomId', params.roomId)
      if (params?.includeCancelled) searchParams.set('includeCancelled', 'true')
      if (params?.myBookingsOnly) searchParams.set('myBookingsOnly', 'true')

      const query = searchParams.toString()
      return apiFetch<{ bookings: Booking[] }>(`${API_BASE}/bookings${query ? `?${query}` : ''}`)
    },

    async get(id: string): Promise<{ booking: Booking }> {
      return apiFetch<{ booking: Booking }>(`${API_BASE}/bookings/${id}`)
    },

    // The caller's own bookings across ALL organisations (personal My Bookings page)
    async mine(): Promise<{ bookings: MyBooking[] }> {
      return apiFetch<{ bookings: MyBooking[] }>(`${API_BASE}/bookings/mine`)
    },

    async create(data: {
      roomId: string
      title?: string
      startTime: string
      endTime: string
      localStartTime?: string
      localEndTime?: string
      notes?: string
      activityTypeId?: string
      activityDescription?: string
      expectedAttendees?: number
      acceptedTermsIds?: string[]
      pliCertificateId?: string
      donationAmount?: number
    }): Promise<{ booking: Booking }> {
      return apiFetch<{ booking: Booking }>(`${API_BASE}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    // Donation context for the booking UI: is the current user a voluntary-donation
    // guest, and how does the org collect donations (link or Stripe)?
    async getDonationContext(orgSlug?: string): Promise<DonationContext> {
      const query = orgSlug ? `?orgSlug=${encodeURIComponent(orgSlug)}` : ''
      return apiFetch<DonationContext>(`${API_BASE}/bookings/donation-context${query}`)
    },

    async update(
      id: string,
      data: {
        title?: string
        startTime?: string
        endTime?: string
        notes?: string
        status?: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'denied'
        statusNote?: string
      }
    ): Promise<{ booking: Booking }> {
      return apiFetch<{ booking: Booking }>(`${API_BASE}/bookings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async delete(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/bookings/${id}`, {
        method: 'DELETE',
      })
    },

    // Pending bookings count (for dashboard badge)
    async getPendingBookingsCount(): Promise<{ count: number }> {
      return apiFetch<{ count: number }>(`${API_BASE}/bookings/pending/count`)
    },

    // Payment reminders
    async getReminders(bookingId: string): Promise<{
      reminders: PaymentReminderLogEntry[]
      autoCancelDate: string | null
    }> {
      return apiFetch<{ reminders: PaymentReminderLogEntry[]; autoCancelDate: string | null }>(
        `${API_BASE}/bookings/${bookingId}/reminders`
      )
    },

    async sendManualReminder(bookingId: string): Promise<{ success: boolean; emailSent: boolean }> {
      return apiFetch<{ success: boolean; emailSent: boolean }>(
        `${API_BASE}/bookings/${bookingId}/reminders/send`,
        { method: 'POST' }
      )
    },

    // Booking history / timeline (admins and approvers)
    async getHistory(bookingId: string): Promise<{ events: BookingHistoryEvent[] }> {
      return apiFetch<{ events: BookingHistoryEvent[] }>(
        `${API_BASE}/bookings/${bookingId}/history`
      )
    },

    // Waive payment (admin) - marks the booking confirmed and paid at £0.00 (e.g. 100% discount)
    async waivePayment(bookingId: string): Promise<{ booking: Booking }> {
      return apiFetch<{ booking: Booking }>(
        `${API_BASE}/bookings/${bookingId}/waive-payment`,
        { method: 'POST' }
      )
    },

    // Access requests
    async getAccessRequestsCount(): Promise<{ count: number }> {
      return apiFetch<{ count: number }>(`${API_BASE}/bookings/access-requests/count`)
    },

    async listAccessRequests(): Promise<{ requests: AccessRequest[] }> {
      return apiFetch<{ requests: AccessRequest[] }>(`${API_BASE}/bookings/access-requests`)
    },

    async updateAccessRequest(
      id: string,
      data: {
        status?: 'approved' | 'denied'
        responseNote?: string
        roomId?: string
        preferredDate?: string
        preferredTime?: string
        message?: string
      }
    ): Promise<{ success: boolean; request?: AccessRequest }> {
      return apiFetch<{ success: boolean; request?: AccessRequest }>(`${API_BASE}/bookings/access-requests/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    // Recurring bookings (admin-only)
    async createRecurring(data: {
      roomId: string
      userId?: string
      paymentType?: 'invoice' | 'donation'
      fixedCost?: number
      donationUrl?: string
      contact?: { phone: string; address: { street: string; city: string; postcode: string; country: string } }
      title?: string
      notes?: string
      patternType: 'weekly' | 'monthly_day' | 'monthly_week'
      dayValue?: number
      weekOfMonth?: number
      dayOfWeek?: number
      startTime: string
      endTime: string
      startDate: string
      endDate?: string
      weekInterval?: number
    }, orgSlug?: string): Promise<{ recurringBooking: RecurringBooking }> {
      return apiFetch<{ recurringBooking: RecurringBooking }>(`${API_BASE}/bookings/recurring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(orgSlug ? { 'X-Org-Slug': orgSlug } : {}) },
        body: JSON.stringify(data),
      })
    },

    async listRecurring(): Promise<{ recurringBookings: RecurringBooking[] }> {
      return apiFetch<{ recurringBookings: RecurringBooking[] }>(`${API_BASE}/bookings/recurring`)
    },

    async recurringPolicy(orgSlug?: string): Promise<RecurringPolicy> {
      return apiFetch<RecurringPolicy>(`${API_BASE}/bookings/recurring/policy`, { headers: orgSlug ? { 'X-Org-Slug': orgSlug } : {} })
    },

    async myRecurring(): Promise<{ recurringBookings: RecurringBooking[] }> {
      return apiFetch<{ recurringBookings: RecurringBooking[] }>(`${API_BASE}/bookings/recurring/mine`)
    },

    async getRecurringOccurrences(params: {
      start: string
      end: string
    }): Promise<{ occurrences: RecurringOccurrence[] }> {
      const searchParams = new URLSearchParams({ start: params.start, end: params.end })
      return apiFetch<{ occurrences: RecurringOccurrence[] }>(
        `${API_BASE}/bookings/recurring/occurrences?${searchParams}`
      )
    },

    async updateRecurring(
      id: string,
      data: {
        title?: string
        notes?: string
        startTime?: string
        endTime?: string
        roomId?: string
        endDate?: string | null
        isActive?: boolean
      },
      orgSlug?: string
    ): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/bookings/recurring/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(orgSlug ? { 'X-Org-Slug': orgSlug } : {}) },
        body: JSON.stringify(data),
      })
    },

    async deleteRecurring(id: string, orgSlug?: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/bookings/recurring/${id}`, {
        method: 'DELETE',
        headers: orgSlug ? { 'X-Org-Slug': orgSlug } : {},
      })
    },

    async addRecurringException(
      recurringId: string,
      data: { date: string; reason?: string },
      orgSlug?: string
    ): Promise<{ exception: RecurringBookingException }> {
      return apiFetch<{ exception: RecurringBookingException }>(
        `${API_BASE}/bookings/recurring/${recurringId}/exceptions`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(orgSlug ? { 'X-Org-Slug': orgSlug } : {}) },
          body: JSON.stringify(data),
        }
      )
    },

    async removeRecurringException(
      recurringId: string,
      exceptionId: string,
      orgSlug?: string
    ): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(
        `${API_BASE}/bookings/recurring/${recurringId}/exceptions/${exceptionId}`,
        {
          method: 'DELETE',
          headers: orgSlug ? { 'X-Org-Slug': orgSlug } : {},
        }
      )
    },
  },

  buildings: {
    async list(): Promise<{ buildings: Building[] }> {
      return apiFetch<{ buildings: Building[] }>(`${API_BASE}/buildings`)
    },

    async get(
      id: string
    ): Promise<{ building: Building; cleaningSchedules: CleaningSchedule[]; rooms: Room[] }> {
      return apiFetch<{ building: Building; cleaningSchedules: CleaningSchedule[]; rooms: Room[] }>(
        `${API_BASE}/buildings/${id}`
      )
    },

    async create(data: {
      name: string
      address?: string
      city?: string
      postcode?: string
      country?: string
      googleMapsUrl?: string
      directions?: string
    }): Promise<{ building: Building }> {
      return apiFetch<{ building: Building }>(`${API_BASE}/buildings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async update(
      id: string,
      data: {
        name?: string
        address?: string
        city?: string
        postcode?: string
        country?: string
        googleMapsUrl?: string
        directions?: string
      }
    ): Promise<{ building: Building }> {
      return apiFetch<{ building: Building }>(`${API_BASE}/buildings/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async delete(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/buildings/${id}`, {
        method: 'DELETE',
      })
    },

    // Cleaning schedules
    async getCleaningSchedules(buildingId: string): Promise<{ schedules: CleaningSchedule[] }> {
      return apiFetch<{ schedules: CleaningSchedule[] }>(
        `${API_BASE}/buildings/${buildingId}/cleaning`
      )
    },

    async createCleaningSchedule(
      buildingId: string,
      data: {
        name?: string
        dayOfWeek: number
        startTime: string
        endTime: string
        isActive?: boolean
      }
    ): Promise<{ schedule: CleaningSchedule }> {
      return apiFetch<{ schedule: CleaningSchedule }>(
        `${API_BASE}/buildings/${buildingId}/cleaning`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      )
    },

    async updateCleaningSchedule(
      buildingId: string,
      scheduleId: string,
      data: {
        name?: string
        dayOfWeek?: number
        startTime?: string
        endTime?: string
        isActive?: boolean
      }
    ): Promise<{ schedule: CleaningSchedule }> {
      return apiFetch<{ schedule: CleaningSchedule }>(
        `${API_BASE}/buildings/${buildingId}/cleaning/${scheduleId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        }
      )
    },

    async deleteCleaningSchedule(
      buildingId: string,
      scheduleId: string
    ): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(
        `${API_BASE}/buildings/${buildingId}/cleaning/${scheduleId}`,
        {
          method: 'DELETE',
        }
      )
    },

    // Opening schedules
    async getOpeningSchedules(buildingId: string): Promise<{ schedules: OpeningSchedule[] }> {
      return apiFetch<{ schedules: OpeningSchedule[] }>(
        `${API_BASE}/buildings/${buildingId}/opening`
      )
    },

    async updateOpeningSchedules(
      buildingId: string,
      schedules: Omit<OpeningSchedule, 'id' | 'buildingId' | 'createdAt'>[]
    ): Promise<{ schedules: OpeningSchedule[] }> {
      return apiFetch<{ schedules: OpeningSchedule[] }>(
        `${API_BASE}/buildings/${buildingId}/opening`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(schedules),
        }
      )
    },
  },

  notifications: {
    async list(): Promise<{ notifications: Notification[] }> {
      return apiFetch<{ notifications: Notification[] }>(`${API_BASE}/notifications`)
    },

    async getUnreadCount(): Promise<{ count: number }> {
      return apiFetch<{ count: number }>(`${API_BASE}/notifications/unread-count`)
    },

    async markAsRead(notificationId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/notifications/${notificationId}/read`, {
        method: 'POST',
      })
    },

    async markAllAsRead(): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/notifications/read-all`, {
        method: 'POST',
      })
    },

    async acceptInvite(
      notificationId: string
    ): Promise<{ success: boolean; orgId?: string; orgName?: string; alreadyMember?: boolean }> {
      return apiFetch<{
        success: boolean
        orgId?: string
        orgName?: string
        alreadyMember?: boolean
      }>(`${API_BASE}/notifications/${notificationId}/accept-invite`, {
        method: 'POST',
      })
    },

    async declineInvite(notificationId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(
        `${API_BASE}/notifications/${notificationId}/decline-invite`,
        {
          method: 'POST',
        }
      )
    },

    async approveAccessRequest(
      notificationId: string
    ): Promise<{ success: boolean; message?: string; alreadyMember?: boolean }> {
      return apiFetch<{ success: boolean; message?: string; alreadyMember?: boolean }>(
        `${API_BASE}/notifications/${notificationId}/approve-access`,
        {
          method: 'POST',
        }
      )
    },

    async dismissAccessRequest(notificationId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(
        `${API_BASE}/notifications/${notificationId}/dismiss-access`,
        {
          method: 'POST',
        }
      )
    },

    async delete(notificationId: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/notifications/${notificationId}`, {
        method: 'DELETE',
      })
    },

    async markByReference(data: {
      requestId?: string
      bookingId?: string
    }): Promise<{ success: boolean; markedCount: number }> {
      return apiFetch<{ success: boolean; markedCount: number }>(
        `${API_BASE}/notifications/mark-by-reference`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      )
    },
  },

  // Global Admin API
  admin: {
    async getStats(): Promise<{ stats: AdminStats }> {
      return apiFetch<{ stats: AdminStats }>(`${API_BASE}/admin/stats`)
    },

    async getBookingStats(
      start?: string,
      end?: string
    ): Promise<{ bookingsByDay: { date: string; count: number }[] }> {
      const params = new URLSearchParams()
      if (start) params.set('start', start)
      if (end) params.set('end', end)
      const query = params.toString()
      return apiFetch<{ bookingsByDay: { date: string; count: number }[] }>(
        `${API_BASE}/admin/stats/bookings${query ? `?${query}` : ''}`
      )
    },

    async listTenants(): Promise<{ tenants: AdminTenant[] }> {
      return apiFetch<{ tenants: AdminTenant[] }>(`${API_BASE}/admin/tenants`)
    },

    async getTenant(id: string): Promise<{ tenant: AdminTenantDetail }> {
      return apiFetch<{ tenant: AdminTenantDetail }>(`${API_BASE}/admin/tenants/${id}`)
    },

    async updateTenant(
      id: string,
      data: { name?: string; slug?: string; settings?: Record<string, unknown> }
    ): Promise<{ tenant: AdminTenant }> {
      return apiFetch<{ tenant: AdminTenant }>(`${API_BASE}/admin/tenants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async deleteTenant(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/admin/tenants/${id}`, {
        method: 'DELETE',
      })
    },

    async listUsers(): Promise<{ users: AdminUser[] }> {
      return apiFetch<{ users: AdminUser[] }>(`${API_BASE}/admin/users`)
    },

    async toggleGlobalAdmin(
      userId: string,
      isGlobalAdmin: boolean
    ): Promise<{
      user: { id: string; email: string; name: string; isGlobalAdmin: boolean }
      message: string
    }> {
      return apiFetch<{
        user: { id: string; email: string; name: string; isGlobalAdmin: boolean }
        message: string
      }>(`${API_BASE}/admin/users/${userId}/admin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isGlobalAdmin }),
      })
    },

    async updateUser(
      userId: string,
      data: { name?: string; email?: string }
    ): Promise<{
      success: boolean
      user: { id: string; email: string; name: string }
      message: string
    }> {
      return apiFetch<{
        success: boolean
        user: { id: string; email: string; name: string }
        message: string
      }>(`${API_BASE}/admin/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async triggerPasswordReset(userId: string): Promise<{ success: boolean; message: string }> {
      return apiFetch<{ success: boolean; message: string }>(
        `${API_BASE}/admin/users/${userId}/reset-password`,
        {
          method: 'POST',
        }
      )
    },

    async deleteUser(userId: string): Promise<{ success: boolean; message: string }> {
      return apiFetch<{ success: boolean; message: string }>(`${API_BASE}/admin/users/${userId}`, {
        method: 'DELETE',
      })
    },
  },

  // Stripe API
  stripe: {
    async getConfig(): Promise<{ enabled: boolean; publishableKey?: string }> {
      return apiFetch<{ enabled: boolean; publishableKey?: string }>(`${API_BASE}/stripe/config`)
    },

    // Admin routes
    async getAdminSettings(): Promise<StripeAdminSettings> {
      return apiFetch<StripeAdminSettings>(`${API_BASE}/stripe/admin/settings`)
    },

    async updateAdminSettings(data: { defaultFeePercent?: string }): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/stripe/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async updateTenantStripe(
      orgId: string,
      data: { roomsiFeePercent?: string; pricingTier?: string }
    ): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/stripe/admin/tenants/${orgId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    // Connect routes
    async getConnectStatus(): Promise<StripeConnectStatus> {
      return apiFetch<StripeConnectStatus>(`${API_BASE}/stripe/connect/status`)
    },

    async getConnectUrl(): Promise<{ url: string; accountId: string }> {
      return apiFetch<{ url: string; accountId: string }>(`${API_BASE}/stripe/connect/url`)
    },

    async getOnboardingUrl(): Promise<{ url: string }> {
      return apiFetch<{ url: string }>(`${API_BASE}/stripe/connect/onboarding-url`)
    },

    async getDashboardUrl(): Promise<{ url: string }> {
      return apiFetch<{ url: string }>(`${API_BASE}/stripe/connect/dashboard-url`)
    },

    async disconnect(): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/stripe/connect/disconnect`, {
        method: 'POST',
      })
    },

    // Checkout routes
    async createCheckoutSession(bookingId: string): Promise<{ sessionId: string; url: string }> {
      return apiFetch<{ sessionId: string; url: string }>(`${API_BASE}/stripe/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId }),
      })
    },

    // Verify payment after returning from Stripe
    async verifyPayment(bookingId: string): Promise<{ status: string; message: string }> {
      return apiFetch<{ status: string; message: string }>(
        `${API_BASE}/stripe/verify-payment/${bookingId}`,
        {
          method: 'POST',
        }
      )
    },

    // Voluntary donation checkout (stripe donation mode)
    async createDonationCheckout(
      bookingId: string,
      amount?: number
    ): Promise<{ sessionId: string; url: string }> {
      return apiFetch<{ sessionId: string; url: string }>(`${API_BASE}/stripe/donation-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bookingId, amount }),
      })
    },

    // Verify donation after returning from Stripe
    async verifyDonation(bookingId: string): Promise<{ status: string; message: string }> {
      return apiFetch<{ status: string; message: string }>(
        `${API_BASE}/stripe/verify-donation/${bookingId}`,
        {
          method: 'POST',
        }
      )
    },

    // Refund a paid booking (admin only)
    async refund(
      bookingId: string
    ): Promise<{ success: boolean; refundId?: string; status?: string; error?: string }> {
      return apiFetch<{ success: boolean; refundId?: string; status?: string; error?: string }>(
        `${API_BASE}/stripe/refund/${bookingId}`,
        {
          method: 'POST',
        }
      )
    },
  },

  // ============================================================================
  // TERMS OF USE
  // ============================================================================
  terms: {
    async list(): Promise<{ terms: TermsOfUse[] }> {
      return apiFetch<{ terms: TermsOfUse[] }>(`${API_BASE}/terms`)
    },

    async getActive(
      scope?: 'organisation' | 'building' | 'room',
      scopeId?: string
    ): Promise<{ terms: TermsOfUse[] }> {
      const params = new URLSearchParams()
      if (scope) params.append('scope', scope)
      if (scopeId) params.append('scopeId', scopeId)
      const query = params.toString() ? `?${params.toString()}` : ''
      return apiFetch<{ terms: TermsOfUse[] }>(`${API_BASE}/terms/active${query}`)
    },

    async get(id: string): Promise<{ terms: TermsOfUse }> {
      return apiFetch<{ terms: TermsOfUse }>(`${API_BASE}/terms/${id}`)
    },

    async create(data: CreateTermsData): Promise<{ terms: TermsOfUse }> {
      return apiFetch<{ terms: TermsOfUse }>(`${API_BASE}/terms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async update(
      id: string,
      data: UpdateTermsData
    ): Promise<{ terms: TermsOfUse; newVersion: boolean }> {
      return apiFetch<{ terms: TermsOfUse; newVersion: boolean }>(`${API_BASE}/terms/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async delete(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/terms/${id}`, {
        method: 'DELETE',
      })
    },

    // Activity Types
    async listActivityTypes(includeInactive?: boolean): Promise<{ activityTypes: ActivityType[] }> {
      const query = includeInactive ? '?includeInactive=true' : ''
      return apiFetch<{ activityTypes: ActivityType[] }>(`${API_BASE}/terms/activity-types${query}`)
    },

    async createActivityType(
      data: CreateActivityTypeData
    ): Promise<{ activityType: ActivityType }> {
      return apiFetch<{ activityType: ActivityType }>(`${API_BASE}/terms/activity-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async updateActivityType(
      id: string,
      data: UpdateActivityTypeData
    ): Promise<{ activityType: ActivityType }> {
      return apiFetch<{ activityType: ActivityType }>(`${API_BASE}/terms/activity-types/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async deleteActivityType(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/terms/activity-types/${id}`, {
        method: 'DELETE',
      })
    },
  },

  // ============================================================================
  // PLI CERTIFICATES
  // ============================================================================
  pli: {
    async list(): Promise<{ certificates: PliCertificate[] }> {
      return apiFetch<{ certificates: PliCertificate[] }>(`${API_BASE}/pli`)
    },

    async listValid(): Promise<{ certificates: PliCertificate[] }> {
      return apiFetch<{ certificates: PliCertificate[] }>(`${API_BASE}/pli/valid`)
    },

    async get(id: string): Promise<{ certificate: PliCertificate }> {
      return apiFetch<{ certificate: PliCertificate }>(`${API_BASE}/pli/${id}`)
    },

    async upload(formData: FormData): Promise<{ certificate: PliCertificate }> {
      const response = await fetch(`${API_BASE}/pli`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      return handleResponse<{ certificate: PliCertificate }>(response)
    },

    async update(
      id: string,
      data: UpdatePliCertificateData
    ): Promise<{ certificate: PliCertificate }> {
      return apiFetch<{ certificate: PliCertificate }>(`${API_BASE}/pli/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async delete(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/pli/${id}`, {
        method: 'DELETE',
      })
    },

    getDownloadUrl(id: string): string {
      return `${API_BASE}/pli/${id}/download`
    },
  },

  // ============================================================================
  // AUDIT LOGS
  // ============================================================================
  audit: {
    async list(params?: {
      page?: number
      pageSize?: number
      entityType?: string
      action?: string
      userId?: string
      search?: string
      startDate?: string
      endDate?: string
    }): Promise<{
      logs: AuditLog[]
      pagination: {
        page: number
        pageSize: number
        total: number
        totalPages: number
      }
    }> {
      const searchParams = new URLSearchParams()
      if (params?.page) searchParams.set('page', params.page.toString())
      if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString())
      if (params?.entityType) searchParams.set('entityType', params.entityType)
      if (params?.action) searchParams.set('action', params.action)
      if (params?.userId) searchParams.set('userId', params.userId)
      if (params?.search) searchParams.set('search', params.search)
      if (params?.startDate) searchParams.set('startDate', params.startDate)
      if (params?.endDate) searchParams.set('endDate', params.endDate)

      const query = searchParams.toString()
      return apiFetch<{
        logs: AuditLog[]
        pagination: { page: number; pageSize: number; total: number; totalPages: number }
      }>(`${API_BASE}/audit${query ? `?${query}` : ''}`)
    },

    async getStats(): Promise<{
      total: number
      last24Hours: number
      byAction: Record<string, number>
      byEntityType: Record<string, number>
    }> {
      return apiFetch<{
        total: number
        last24Hours: number
        byAction: Record<string, number>
        byEntityType: Record<string, number>
      }>(`${API_BASE}/audit/stats`)
    },

    async getEntityHistory(entityType: string, entityId: string): Promise<{ logs: AuditLog[] }> {
      return apiFetch<{ logs: AuditLog[] }>(`${API_BASE}/audit/entity/${entityType}/${entityId}`)
    },

    async getUsers(): Promise<{
      users: { userId: string | null; userName: string; userEmail: string | null }[]
    }> {
      return apiFetch<{
        users: { userId: string | null; userName: string; userEmail: string | null }[]
      }>(`${API_BASE}/audit/users`)
    },

    getExportUrl(params?: {
      entityType?: string
      action?: string
      userId?: string
      startDate?: string
      endDate?: string
      format?: 'csv' | 'json'
    }): string {
      const searchParams = new URLSearchParams()
      if (params?.entityType) searchParams.set('entityType', params.entityType)
      if (params?.action) searchParams.set('action', params.action)
      if (params?.userId) searchParams.set('userId', params.userId)
      if (params?.startDate) searchParams.set('startDate', params.startDate)
      if (params?.endDate) searchParams.set('endDate', params.endDate)
      if (params?.format) searchParams.set('format', params.format)

      const query = searchParams.toString()
      return `${API_BASE}/audit/export${query ? `?${query}` : ''}`
    },
  },

  // ============================================================================
  // INVOICES
  // ============================================================================
  invoices: {
    async list(params?: {
      status?: string
      userId?: string
    }): Promise<{ invoices: Invoice[] }> {
      const searchParams = new URLSearchParams()
      if (params?.status) searchParams.set('status', params.status)
      if (params?.userId) searchParams.set('userId', params.userId)
      const query = searchParams.toString()
      return apiFetch<{ invoices: Invoice[] }>(
        `${API_BASE}/invoices${query ? `?${query}` : ''}`
      )
    },

    async get(id: string): Promise<{ invoice: InvoiceDetail }> {
      return apiFetch<{ invoice: InvoiceDetail }>(`${API_BASE}/invoices/${id}`)
    },

    async create(data: {
      userId: string
      notes?: string
      lineItems: Array<{ roomId: string; startTime: string; endTime: string }>
    }): Promise<{ invoice: Invoice }> {
      return apiFetch<{ invoice: Invoice }>(`${API_BASE}/invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async update(
      id: string,
      data: {
        notes?: string
        totalAmount?: number
        discountPercent?: number
        lineItems?: Array<{ roomId: string; startTime: string; endTime: string }>
      }
    ): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/invoices/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      })
    },

    async send(id: string): Promise<{ success: boolean; checkoutUrl: string }> {
      return apiFetch<{ success: boolean; checkoutUrl: string }>(
        `${API_BASE}/invoices/${id}/send`,
        { method: 'POST' }
      )
    },

    async resend(id: string): Promise<{ success: boolean; checkoutUrl: string }> {
      return apiFetch<{ success: boolean; checkoutUrl: string }>(
        `${API_BASE}/invoices/${id}/resend`,
        { method: 'POST' }
      )
    },

    async cancel(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/invoices/${id}/cancel`, {
        method: 'POST',
      })
    },

    async refund(id: string): Promise<{ success: boolean; refundId?: string }> {
      return apiFetch<{ success: boolean; refundId?: string }>(
        `${API_BASE}/invoices/${id}/refund`,
        { method: 'POST' }
      )
    },

    async delete(id: string): Promise<{ success: boolean }> {
      return apiFetch<{ success: boolean }>(`${API_BASE}/invoices/${id}`, {
        method: 'DELETE',
      })
    },
  },
}

// Admin types
export interface AdminStats {
  totalUsers: number
  totalOrgs: number
  activeMemberships: number
  totalRooms: number
  totalBuildings: number
  totalBookings: number
  bookingsToday: number
}

export interface AdminTenant {
  id: string
  name: string
  slug: string
  settings: Record<string, unknown>
  createdAt: string
  updatedAt: string
  memberCount: number
  roomCount: number
  buildingCount: number
  bookingCount: number
}

export interface AdminTenantDetail extends AdminTenant {
  members: {
    id: string
    userId: string
    email: string
    name: string
    role: string
    accountType: string
    status: string
    joinedAt?: string
  }[]
  rooms: { id: string; name: string; slug: string; isActive: boolean; isPublic: boolean }[]
  buildings: { id: string; name: string; slug: string }[]
}

export interface AdminUser {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  isGlobalAdmin: boolean
  createdAt: string
  memberships: {
    orgId: string
    orgName: string
    orgSlug: string
    role: string
    status: string
  }[]
}

// Room types
export interface Room {
  id: string
  buildingId?: string | null
  name: string
  slug: string
  descriptionHtml?: string | null
  capacity: number
  hourlyRate: number
  amenities: string[]
  images: { url: string; alt?: string }[]
  heroImageIndex: number
  location?: string | null
  bookingLeadTime?: number | null
  maxBookingDuration?: number | null
  requiresApproval?: boolean | null
  accessibilityFeatures?: string[] | null
  accessibilityNotes?: string | null
  isPublic: boolean
  isActive: boolean
  createdAt: string
  updatedAt: string
}

// Building types
export interface Building {
  id: string
  name: string
  slug: string
  address?: string | null
  city?: string | null
  postcode?: string | null
  country?: string | null
  googleMapsUrl?: string | null
  directions?: string | null
  accessibilityFeatures?: string[] | null
  accessibilityNotes?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CleaningSchedule {
  id: string
  buildingId: string
  name: string
  dayOfWeek: number
  startTime: string
  endTime: string
  isActive: boolean
  createdAt: string
}

export interface OpeningSchedule {
  id: string
  buildingId: string
  dayOfWeek: number
  openTime: string
  closeTime: string
  isActive: boolean
  createdAt: string
}

export interface RoomSchedule {
  id: string
  roomId: string
  dayOfWeek: number
  openTime: string
  closeTime: string
  isActive: boolean
}

export interface AccessRequest {
  id: string
  name: string
  email: string
  phone: string | null
  roomId: string | null
  roomName: string | null
  preferredDate: string | null
  preferredTime: string | null
  message: string | null
  status: 'pending' | 'approved' | 'denied'
  // Outcome of an approved request: did it actually become a booking?
  // 'booked' = a matching booking exists; 'awaiting' = invite still outstanding;
  // 'not_booked' = stranded (approved but no invite and no booking). null for non-approved.
  bookingState?: 'booked' | 'awaiting' | 'not_booked' | null
  handledBy: string | null
  handledByName: string | null
  handledAt: string | null
  responseNote: string | null
  createdAt: string
}

export interface BookingUser {
  id: string
  name: string
  email: string
  phone?: string | null
  avatarUrl?: string | null
  role?: 'admin' | 'approver' | 'guest'
  accountType?: 'guest' | 'room_owner'
}

export interface Booking {
  id: string
  roomId: string
  userId: string
  createdByUserId?: string | null
  approvedByUserId?: string | null
  bookingSource?: 'guest_request' | 'admin_direct'
  title?: string | null
  startTime: string
  endTime: string
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'denied'
  statusChangedAt?: string | null
  statusNote?: string | null
  totalPrice: number
  paymentStatus?: 'not_required' | 'pending' | 'paid' | 'refunded' | 'failed' | null
  stripePaymentIntentId?: string | null
  immediatePaymentDeadline?: string | null
  notes?: string | null
  // Voluntary donation (stripe donation mode)
  isVoluntaryDonation?: boolean
  recurringBookingId?: string | null
  recurringDate?: string | null
  recurringPaymentType?: 'none' | 'invoice' | 'donation' | null
  donationAmount?: number | string | null
  donationPaidAt?: string | null
  donationBreakdown?: {
    total: number
    stripeFee: number | null
    roomsiFee: number | null
    roomsiFeePercent: number
    orgReceives: number | null
  } | null
  createdAt: string
  updatedAt: string
  // Enriched user info (populated by API)
  bookedBy?: BookingUser | null
  createdBy?: { id: string; name: string; email: string } | null
  approvedBy?: { id: string; name: string; email: string } | null
  // Privacy flag - true when booking details are hidden for special guests
  isRedacted?: boolean
}

// A booking on the personal My Bookings page: the user's own booking in any org,
// enriched with the names needed to display it without an org context.
export interface MyBooking extends Booking {
  orgName: string | null
  orgSlug: string | null
  roomName: string | null
  buildingName: string | null
}

export interface PaymentReminderLogEntry {
  id: string
  reminderKey: string
  type: 'reminder' | 'urgent' | 'auto_cancel'
  emailSent: boolean
  sentAt: string
}

export interface BookingHistoryEvent {
  id: string
  action: string
  label: string
  actor: string
  at: string
  note: string | null
}

export interface Blackout {
  id: string
  roomId: string
  startDate: string
  endDate: string
  reason?: string | null
}

export interface RoomActivity {
  id: string
  type:
    | 'booking_requested'
    | 'booking_confirmed'
    | 'booking_cancelled'
    | 'booking_denied'
    | 'booking_completed'
  timestamp: string
  booking: {
    id: string
    title: string | null
    startTime: string
    endTime: string
    status: string
    statusNote: string | null
    totalPrice: number
    paymentStatus: 'not_required' | 'pending' | 'paid' | 'refunded' | 'failed' | null
    createdAt: string
  }
  bookedBy: {
    id: string
    name: string
    email: string
  } | null
  createdBy: {
    id: string
    name: string
    email: string
  } | null
  approvedBy: {
    id: string
    name: string
    email: string
  } | null
  cancelledBy: {
    id: string
    name: string
    email: string
  } | null
  refundedBy: {
    id: string
    name: string
    email: string
  } | null
}

export interface CreateRoomData {
  name: string
  descriptionHtml?: string
  capacity?: number
  hourlyRate?: number
  amenities?: string[]
  location?: string
  bookingLeadTime?: number | null
  maxBookingDuration?: number | null
  requiresApproval?: boolean | null
  accessibilityFeatures?: string[] | null
  accessibilityNotes?: string | null
  isPublic?: boolean
  buildingId?: string
}

export interface CreateBlockData {
  name: string
  dayOfWeek: number
  startTime: string
  endTime: string
  isActive?: boolean
}

export interface CreateBlackoutData {
  startDate: string
  endDate: string
  reason?: string
}

export interface OrgSettings {
  currency?: string
  timezone?: string
  bookingLeadTime?: number // hours
  maxBookingDuration?: number // hours
  requireApprovalByDefault?: boolean
  allowPublicBookings?: boolean // allow non-members to book without invite
  galleryIntro?: string // text displayed on public gallery page
  donationUrl?: string // public donation page for voluntary-donation bookings
  donationMode?: 'link' | 'stripe' // how voluntary-donation guests donate
  paymentReminders?: PaymentReminderSettings
}

export interface DonationContext {
  voluntaryDonation: boolean
  donationMode: 'link' | 'stripe'
  stripeReady: boolean
  roomsiFeePercent: number
  currency: string
  orgName: string
}

export interface PaymentReminderSettings {
  enabled: boolean
  autoCancelDaysBefore: number
  immediatePaymentHours: number
  reminders: PaymentReminderRule[]
}

export interface PaymentReminderRule {
  id: string
  reference: 'after_approval' | 'before_booking'
  days: number
  enabled: boolean
}

export interface Organisation {
  id: string
  name: string
  slug: string
  settings: OrgSettings
  createdAt: string
}

export interface Currency {
  code: string
  name: string
  symbol: string
}
export interface PublicOrg {
  name: string
  slug: string
  currency: string
  galleryIntro?: string
  allowPublicBookings?: boolean
}

export interface PublicBuilding {
  name: string
  address?: string | null
  googleMapsUrl?: string | null
  directions?: string | null
}

export interface PublicRoomsResponse {
  organisation: PublicOrg
  rooms: Room[]
  buildings: Record<string, PublicBuilding>
}

export interface PublicRoomResponse {
  organisation: PublicOrg
  room: Room
  building?: PublicBuilding | null
  schedules: RoomSchedule[]
}

export interface UserAttributes {
  requiresApproval?: boolean
  discountCode?: string | null
  canBookDirectly?: boolean
}

export interface OrgUser {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  role: 'admin' | 'approver' | 'guest'
  accountType: 'guest' | 'room_owner'
  attributes: UserAttributes
  status: 'pending' | 'active' | 'suspended'
  invitedAt?: string | null
  joinedAt?: string | null
  createdAt: string
}

export interface Guest {
  id: string
  email: string
  name: string
  avatarUrl?: string | null
  guestType: 'guest' | 'special_guest' | 'approver' | 'admin'
  membershipStatus: 'pending' | 'active' | 'suspended' | null
  skipApproval: boolean
  discountPercent: number | null
  canBookOutOfHours: boolean
  totalBookings: number
  confirmedBookings: number
  pendingBookings: number
  cancelledBookings: number
  deniedBookings: number
  completedBookings: number
  firstBooking: string | null
  lastBooking: string | null
  createdAt: string
  lastBookingAt: string | null
}

export interface GuestDetail extends Guest {
  phone?: string | null
  address?: string | null
  membershipId: string | null
}

export interface GuestBooking {
  id: string
  roomId: string
  roomName: string
  title: string | null
  startTime: string
  endTime: string
  status: string
  totalPrice: string
  createdAt: string
}

// Unified People types
export interface Person {
  id: string
  email: string
  name: string
  avatarUrl: string | null
  role: 'admin' | 'approver' | 'special_guest' | 'guest'
  status: 'pending' | 'active' | 'suspended' | 'invited'
  skipApproval: boolean
  discountPercent: number | null
  canBookOutOfHours: boolean
  voluntaryDonation: boolean
  canCreateRecurringBookings?: boolean
  recurringPaymentType?: 'invoice' | 'donation'
  recurringFixedCost?: number | null
  recurringDonationUrl?: string | null
  totalBookings: number
  confirmedBookings: number
  pendingBookings: number
  lastBooking: string | null
  joinedAt: string | null
  invitedAt: string | null
  createdAt: string
}

export interface PersonDetail extends Person {
  phone?: string | null
  address?: string | null
  membershipId: string | null
}

export interface PersonBooking {
  id: string
  roomId: string
  roomName: string
  title: string | null
  startTime: string
  endTime: string
  status: string
  totalPrice: string
  createdAt: string
}

export interface Notification {
  id: string
  userId: string
  type:
    | 'org_invite'
    | 'booking_approved'
    | 'booking_rejected'
    | 'booking_cancelled'
    | 'access_request'
    | 'retention_warning'
    | 'system'
    | 'payment_confirmed'
    | 'payment_received'
    | 'payment_refunded'
  title: string
  message: string
  data?: {
    inviteToken?: string
    orgId?: string
    orgName?: string
    inviterName?: string
    role?: string
    bookingId?: string
    requestId?: string
    requesterEmail?: string
    requesterName?: string
    roomName?: string
    requestMessage?: string
    [key: string]: unknown
  }
  readAt?: string | null
  actionedAt?: string | null
  createdAt: string
}

// Stripe types
export interface StripeAdminSettings {
  settings: {
    defaultFeePercent: string
    stripeConfigured: boolean
  }
  connectedAccounts: StripeConnectedAccount[]
  totalOrgs: number
  connectedCount: number
}

export interface StripeConnectedAccount {
  orgId: string
  orgName: string
  orgSlug: string
  stripeAccountId: string
  chargesEnabled?: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  roomsiFeePercent: string
  pricingTier: string
  error?: string
}

export interface StripeConnectStatus {
  connected: boolean
  stripeConfigured: boolean
  stripeAccountId?: string
  chargesEnabled?: boolean
  payoutsEnabled?: boolean
  detailsSubmitted?: boolean
  dashboardUrl?: string
  error?: string
}

// ============================================================================
// TERMS OF USE TYPES
// ============================================================================

export interface TermsOfUse {
  id: string
  orgId: string
  scope: 'organisation' | 'building' | 'room'
  scopeId: string | null
  title: string
  content: string
  version: number
  contentHash: string
  isActive: boolean
  createdAt: string
  updatedAt: string
  createdBy: string | null
  updatedBy: string | null
}

export interface CreateTermsData {
  scope: 'organisation' | 'building' | 'room'
  scopeId?: string
  title: string
  content: string
}

export interface UpdateTermsData {
  title?: string
  content?: string
  isActive?: boolean
}

// ============================================================================
// ACTIVITY TYPES
// ============================================================================

export interface ActivityType {
  id: string
  orgId: string
  name: string
  description: string | null
  requiresPli: boolean
  sortOrder: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateActivityTypeData {
  name: string
  description?: string
  requiresPli?: boolean
  sortOrder?: number
}

export interface UpdateActivityTypeData {
  name?: string
  description?: string
  requiresPli?: boolean
  sortOrder?: number
  isActive?: boolean
}

// ============================================================================
// PLI CERTIFICATES
// ============================================================================

export interface PliCertificate {
  id: string
  userId: string
  insurerName: string
  policyNumber: string | null
  coverageAmount: string | null
  validFrom: string | null
  validUntil: string | null
  fileName: string
  filePath: string
  fileSize: number | null
  mimeType: string | null
  notes: string | null
  createdAt: string
  updatedAt: string
  retentionExpiresAt: string | null
}

export interface UpdatePliCertificateData {
  insurerName?: string
  policyNumber?: string | null
  coverageAmount?: string | null
  validFrom?: string | null
  validUntil?: string | null
  notes?: string | null
}

// ============================================================================
// AUDIT LOG TYPES
// ============================================================================

export interface AuditLog {
  id: string
  action: string
  entityType: string
  entityId: string
  changedBy: string
  changedAt: string
  summary: string
  details: {
    field: string
    from: unknown
    to: unknown
  }[]
}

// ============================================================================
// RECURRING BOOKING TYPES
// ============================================================================

export interface RecurringBooking {
  id: string
  tenantId: string
  roomId: string
  createdByUserId: string | null
  userId?: string | null
  guestName?: string | null
  orgSlug?: string
  orgName?: string
  roomSlug?: string
  materialized?: boolean
  currency?: string
  timeZone?: string
  paymentType?: 'none' | 'invoice' | 'donation'
  fixedCost?: string | null
  donationUrl?: string | null
  title: string | null
  notes: string | null
  patternType: 'weekly' | 'monthly_day' | 'monthly_week'
  dayOfWeek: number | null
  dayOfMonth: number | null
  weekOfMonth: number | null
  weekInterval: number | null
  dayValue?: number | null // alias for dayOfWeek or dayOfMonth depending on pattern
  startTime: string
  endTime: string
  startDate: string
  endDate: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  // Enriched
  roomName?: string
  createdBy?: { id: string; name: string; email: string } | null
  exceptions?: RecurringBookingException[]
}

export interface RecurringPolicy {
  allowed: boolean
  isAdmin: boolean
  paymentType: 'invoice' | 'donation'
  fixedCost: number | null
  donationUrl: string | null
  timeZone: string
  currency: string
  maxEndDate: string
  hasContactDetails: boolean
  skipApproval: boolean
  requireApprovalByDefault: boolean
}

export interface RecurringOccurrence {
  id: string
  recurringBookingId: string
  roomId: string
  title: string | null
  date: string
  startTime: string
  endTime: string
  patternType: string
  status: string
}

export interface RecurringBookingException {
  id: string
  recurringBookingId?: string
  exceptionDate: string
  reason: string | null
  cancelledByUserId?: string | null
}

// ============================================================================
// INVOICE TYPES
// ============================================================================

export interface Invoice {
  id: string
  tenantId: string
  invoiceNumber: number
  invoiceRef: string
  userId: string
  guestDisplayName: string | null
  guestName: string
  guestEmail: string
  createdByUserId: string | null
  status: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled' | 'refunded'
  subtotalAmount: string
  discountPercent: string | null
  totalAmount: string
  dueDate: string
  paidAt: string | null
  sentAt: string | null
  notes: string | null
  lineItemCount: number
  createdAt: string
  updatedAt: string
}

export interface InvoiceLineItem {
  id: string
  invoiceId: string
  bookingId: string | null
  roomId: string
  roomName: string
  description: string
  startTime: string
  endTime: string
  hourlyRate: string
  hours: string
  lineTotal: string
  sortOrder: number
}

export interface InvoiceDetail extends Invoice {
  createdByName: string
  stripeCheckoutSessionId: string | null
  stripePaymentIntentId: string | null
  paymentBreakdown: {
    total: number
    stripeFee: number | null
    roomsiFee: number | null
    roomsiFeePercent: number
    orgReceives: number | null
  } | null
  lineItems: InvoiceLineItem[]
}
